import type { Instrument, Timeframe } from "@trader/shared/MarketData"
import type { StartPolicy } from "@trader/shared/Money"
import { useState } from "react"
import { Modal } from "../../ui/Modal.tsx"
import { formatClock } from "../trading/format.ts"
import { NewAccountDialog } from "../trading/NewAccountDialog.tsx"
import type { StrategyInstance } from "../workspace/workspaceStore.ts"
import { accountLabel, PRODUCT_LABELS, sizingNote, START_LABELS, timeframeNote } from "./attachRunner.ts"
import { useAttachRunner } from "./useAttachRunner.ts"

const START_POLICIES: ReadonlyArray<StartPolicy> = ["waitForFlat", "adopt"]

/**
 * "Paper trade…": attaches the chart's strategy, with its inputs and properties,
 * to a paper account. From the next closed bar it re-runs the strategy and trades
 * the account to the strategy's position (docs/design/0005, P3).
 */
export function AttachRunnerDialog({ defaultProperties, instrument, onClose, strategy, timeframe, title }: {
  readonly strategy: StrategyInstance
  readonly title: string
  /** The strategy's own properties (its latest run), under the pane's overrides. */
  readonly defaultProperties: Readonly<Record<string, unknown>>
  readonly instrument: Instrument
  readonly timeframe: Timeframe
  readonly onClose: () => void
}) {
  const state = useAttachRunner(strategy, instrument, timeframe)
  const { account, accounts, attached } = state
  const [creating, setCreating] = useState(false)

  if (creating) {
    return (
      <NewAccountDialog
        defaultCurrency={instrument.currency}
        defaultInstrument={instrument.id}
        defaultTimeframe={timeframe}
        onClose={() => setCreating(false)}
        onCreated={(created) => {
          state.accountCreated(created)
          setCreating(false)
        }}
      />
    )
  }

  if (attached !== null) {
    const clock = account?.clock
    const replay = clock?._tag === "Replay" ? clock : null
    return (
      <Modal title={`Paper trade ${title}`} onClose={onClose}>
        <div className="settings-form" role="status">
          <p>
            <strong>{attached.strategyName}</strong> now paper trades {attached.instrumentId} ({attached.product}) on{" "}
            {account?.name ?? attached.accountId}: {attached.status._tag === "Arming" ? "waiting for its next trade" : attached.status._tag}.
          </p>
          {replay !== null && replay.status !== "Playing" && (
            <p>
              The account replays history and is paused at {formatClock(replay.now)}.
              Play (or Step) moves it forward; the strategy trades at its next signal, and its orders and fills show
              in Paper Trading.
            </p>
          )}
          {replay === null && (
            <p className="muted">
              A Realtime account trades on new bars as they arrive. There is no live data yet (stored history only),
              so use a Replay account to watch the strategy trade now.
            </p>
          )}
          {state.playError && <p className="down" role="alert">{state.playError}</p>}
          <footer className="settings-actions">
            <span className="toolbar-spacer" />
            <button type="button" onClick={onClose}>Done</button>
            {replay !== null && (
              <button
                type="button"
                className="primary"
                onClick={() => void state.showAndPlay().then((ok) => ok && onClose())}
              >
                {replay.status === "Playing" ? "Show in Paper Trading" : "Play the replay (5×)"}
              </button>
            )}
          </footer>
        </div>
      </Modal>
    )
  }

  const target = state.target
  if (target === null || target._tag === "Unsaved") {
    return (
      <Modal title={`Paper trade ${title}`} onClose={onClose}>
        <div className="settings-form" role="status">
          {target === null ? <p className="muted">Checking the strategy…</p> : <p>{target.message}</p>}
          <footer className="settings-actions">
            <span className="toolbar-spacer" />
            {target?._tag === "Unsaved" && target.saved !== null && (
              <button type="button" onClick={state.useSavedVersion}>Paper trade version {target.saved.version} instead</button>
            )}
            <button type="button" className="primary" onClick={onClose}>Close</button>
          </footer>
        </div>
      </Modal>
    )
  }

  const note = timeframeNote(account, timeframe)
  const sizing = sizingNote({ ...defaultProperties, ...strategy.properties })
  return (
    <Modal title={`Paper trade ${title}`} onClose={onClose}>
      <form
        noValidate
        className="settings-form"
        aria-label="Paper trade"
        onSubmit={(event) => {
          event.preventDefault()
          void state.submit()
        }}
      >
        {state.loadError && <p className="down">{state.loadError}</p>}
        {accounts === null && !state.loadError && <p className="muted">Loading your paper accounts…</p>}
        {accounts !== null && accounts.length === 0 && (
          <>
            <p>
              A strategy paper trades on a paper account in {instrument.currency}, and you have none yet. Create one: a
              Replay account steps through stored history, so you can watch the strategy trade right away.
            </p>
            <p>
              <button type="button" className="primary" onClick={() => setCreating(true)}>
                Create a paper account in {instrument.currency}…
              </button>
            </p>
          </>
        )}
        {accounts !== null && accounts.length > 0 && (
          <>
            <label>
              Account
              <select value={account?.id ?? ""} onChange={(event) => state.setAccountId(event.target.value)}>
                {accounts.map((candidate) => <option key={candidate.id} value={candidate.id}>{accountLabel(candidate)}</option>)}
              </select>
            </label>
            <label>
              Product
              <select
                value={state.product}
                onChange={(event) => {
                  const product = state.products.find((candidate) => candidate === event.target.value)
                  if (product !== undefined) state.setProduct(product)
                }}
              >
                {state.products.map((product) => <option key={product} value={product}>{PRODUCT_LABELS[product]}</option>)}
              </select>
            </label>
            <label>
              Start
              <select
                value={state.startPolicy}
                onChange={(event) => {
                  const policy = START_POLICIES.find((candidate) => candidate === event.target.value)
                  if (policy !== undefined) state.setStartPolicy(policy)
                }}
              >
                {START_POLICIES.map((policy) => <option key={policy} value={policy}>{START_LABELS[policy]}</option>)}
              </select>
            </label>
            <label className={state.shortable ? "" : "disabled"} title={state.shortable ? "" : "Cash only sells what it holds; choose Intraday or Carryforward to sell short"}>
              Long only
              <input
                type="checkbox"
                checked={state.longOnly}
                disabled={!state.shortable}
                onChange={(event) => state.setLongOnly(event.target.checked)}
              />
            </label>
            <p className="muted">
              {state.longOnly ? "Short entries only close the long position. " : ""}
              It uses this chart's inputs and properties{note ? `. ${note}` : "."}
              {target.note ? ` ${target.note}` : ""}
            </p>
            {sizing && <p className="muted">{sizing}</p>}
            {account?.clock._tag === "Realtime" && (
              <p className="down">
                This is a Realtime account: it trades on new bars as they arrive, and with stored history only (no live
                data yet) none arrive. Pick a Replay account to see the strategy trade now.
              </p>
            )}
            <p>
              <button type="button" className="link" onClick={() => setCreating(true)}>+ New paper account…</button>
            </p>
          </>
        )}
        {state.error && <p className="down" role="alert">{state.error}</p>}
        <footer className="settings-actions">
          <span className="toolbar-spacer" />
          <button type="button" onClick={onClose}>Cancel</button>
          <button type="submit" className="primary" disabled={account === undefined || state.submitting}>
            {state.submitting ? "Starting…" : "Start paper trading"}
          </button>
        </footer>
      </form>
    </Modal>
  )
}
